import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Tag,
  Button,
  Badge,
  Card,
} from "antd";
import {
  CrudFilters,
  HttpError,
  useGetIdentity,
  useNavigation,
} from "@refinedev/core";
import { PlusOutlined } from "@ant-design/icons";
import { PaginationTotal } from "@/components/pagination-total";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { SellersUserPermissionsCreate } from "./userPermissionsCreate";
import { User } from "@/types/schema/schema.types";
import { useNavigate } from "react-router-dom";
import { KeyRound } from "@/components/icon/Key";
import { NormalModal, SectionForbidden } from "@/components";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text, Link } = Typography;
const { Search } = Input;

export const SellersUserPermissionsList: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const { data: dataLoguinUser } = useGetIdentity<User>();
  const { smartMenuName } = useContext(SmartMenuContext);
  const [typeIdentifier, setTypeIdentifier] = useState("seller");

  const storeIdentifier = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const { name_search } = params;

    filters.push({
      field: "search",
      operator: "contains",
      value: name_search,
    });

    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch, error },
    searchFormProps,
    tableProps,
  } = useTable<HttpError>({
    resource: `permissions/${typeIdentifier}/${storeIdentifier}/users`,
    pagination: {
      pageSize: FILAS_POR_PAGINA,
    },
    sorters: {
      initial: [
        {
          field: "user.firstname",
          order: "asc",
        },
      ],
    },
    onSearch: onSearch,
    filters: {
      permanent: [{ field: "search", operator: "eq", value: "" }],
    },
  });

  /**
   * El backend responde 403 cuando el operador no tiene `seller_permission` sobre este seller —es
   * el permiso "Operadores del Seller"— y también cuando no es operador del seller.
   *
   * Sin este corte la pantalla quedaba mintiendo: `isLoadingTable` es `!data`, y con un 403 `data`
   * nunca llega, así que la tabla se quedaba en su estado vacío ("no hay datos") con el buscador y
   * el botón de alta intactos. El toast del interceptor se va en segundos y no queda rastro de que
   * el problema era de permisos.
   */
  const isForbidden = (error as HttpError | undefined)?.statusCode === 403;

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : data?.data || []),
    [isLoadingTable, data],
  );

  const columns: TableColumnsType<any> = [
    {
      key: "firstname",
      title: t("products.show.tableName"),
      sorter: (a, b) => a.user.firstname.localeCompare(b.user.firstname),
      dataIndex: "user.firstname",
      render: (_, record) => {
        return (
          <Flex justify="space-between" align="center">
            <Text
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {record?.user?.firstname} {record?.user?.lastname}
              {dataLoguinUser?.email === record?.user?.email && (
                <Tag style={{ color: token.colorPrimary, marginLeft: "8px" }}>
                  {t("products.show.you")}
                </Tag>
              )}
            </Text>
          </Flex>
        );
      },
      width: "30%",
    },
    {
      key: "email",
      title: t("pages.login.fields.email"),
      dataIndex: "user.email",
      render: (_, record) => {
        return <Text>{record?.user?.email}</Text>;
      },
      width: "35%",
    },
    {
      key: "whatsapp",
      title: t("linkedProducts.modal.whatsapp") || "WhatsApp",
      dataIndex: "user.whatsapp",

      render: (_, record) => {
        return (
          <Text>{record?.user?.whatsapp ? record?.user?.whatsapp : "-"}</Text>
        );
      },
      width: "25%",
    },
    {
      key: "status",
      title: t("orders.table.status") || "Status",
      dataIndex: "isActive",
      sorter: (a, b) => a.isActive - b.isActive,
      align: "center",
      width: "10%",
      render: (_, record) => {
        return (
          <Badge
            status={record?.isActive ? "success" : "default"}
            text={
              record.isActive
                ? t("filter.products.labelStatusActive")
                : t("filter.products.labelStatusDesactive")
            }
          />
        );
      },
    },
  ];

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="name_search">
            <Search
              placeholder={t("search.placeholder.byUserEmailWhatsapp")}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    name_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
          className="add-user-button"
        >
          <NormalModal
            title={t("button.add.operator")}
            footer={null}
            trigger={
              <Button icon={<PlusOutlined />} iconPosition="end" type="primary">
                {t("button.add.operator")}
              </Button>
            }
          >
            <SellersUserPermissionsCreate
              t={t}
              refetch={refetch}
              sectionType="permission"
            />
          </NormalModal>
        </Flex>
      </Flex>
    </>
  ));

  /**
   * **El alto de las filas, lo que queda de pantalla** (xcons-docs#599), como en el Catálogo.
   * Sin tope la tabla crecía con cada operador y empujaba la página; ahora las filas scrollean
   * adentro y el paginador queda a la vista. Se vuelve a medir al cambiar el tamaño de la ventana.
   */
  const contenedor = useRef<HTMLDivElement>(null);
  const [altoFilas, setAltoFilas] = useState<number | null>(null);
  useEffect(() => {
    const el = contenedor.current;
    if (!el) return;
    const medir = () => {
      const filas = el.querySelector<HTMLElement>(".ant-table-body");
      if (!filas) return;
      const paginador = el.querySelector<HTMLElement>(".ant-table-pagination");
      // El paginador con sus márgenes (16 y 16) y el margen de abajo de la página (24).
      const pie = (paginador?.offsetHeight ?? 32) + 32 + 24;
      setAltoFilas(
        Math.max(
          240,
          Math.floor(
            window.innerHeight - filas.getBoundingClientRect().top - pie,
          ),
        ),
      );
    };
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, [isFetching, isForbidden]);

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/sellers/operators/${storeIdentifier}/${record?.user?.userId}`, {
      state: { record: record },
    });
  };

  const generateRowKey = (record: any) => {
    if (record && typeof record?.user?.userId !== "undefined") {
      return `${record?.user?.userId}`;
    }
    console.warn(
      "generateRowKey: userId no encontrado en el registro. Registro:",
      record,
    );
    return `missing-userId-${Math.random().toString(36).substring(7)}`;
  };

  return (
    <>
      <Title level={3} style={{ display: "flex", alignItems: "center" }}>
        <KeyRound style={{ marginRight: "10px" }} />
        {`${t("contacts.tag.sellerusers")}`}
      </Title>
      {isForbidden ? (
        // El título queda arriba a propósito: el usuario tiene que ver DÓNDE está y que la sección
        // existe, sólo que no le corresponde. El resto —buscador, alta, tabla— no se renderiza,
        // porque cada uno de esos controles termina en otro 403.
        <SectionForbidden />
      ) : (
        <div
          ref={contenedor}
          className="tabla-al-alto-de-pantalla"
          style={
            altoFilas
              ? ({ "--alto-filas": `${altoFilas}px` } as React.CSSProperties)
              : undefined
          }
        >
          <Table
            {...tableProps}
            style={{ cursor: "pointer" }}
            // El `y` sólo hace que las filas scrolleen adentro; el alto real lo pone `--alto-filas`.
            scroll={{ x: true, y: 55 * 10.5 }}
            title={() => <HeaderRow />}
            rowKey={(record) => generateRowKey(record)}
            onRow={(record) => ({
              onClick: () => {
                handleRowClick(record);
              },
            })}
            dataSource={dataSource}
            columns={columns}
            loading={isFetching}
            locale={{
              triggerAsc: t("table.triggerAsc"),
              triggerDesc: t("table.triggerDesc"),
              cancelSort: t("table.cancelSort"),
            }}
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              pageSizeOptions: OPCIONES_POR_PAGINA,
              showQuickJumper: false,
              locale: {
                items_per_page: t("table.itemsPerPage"),
                prev_page: t("table.prevPage"),
                next_page: t("table.nextPage"),
                jump_to: t("table.jumpTo"),
                jump_to_confirm: t("table.jumpToConfirm"),
                page: t("table.page"),
                next_5: t("table.next5"),
                prev_5: t("table.prev5"),
              },
              showTotal: (total) => (
                <PaginationTotal total={total} entityName={t("operators")} />
              ),
            }}
          />
        </div>
      )}
    </>
  );
};
